import { ReactElement, useEffect, useState } from 'react'
import { PageLoading } from 'components/extend/PageLoading'
import { usePrevious } from 'hooks/usePrevious'

import { PlaceholderCell } from './PlaceholderCell'

interface IProps<Data> {
  children: (props: Data) => ReactElement | null
  checkAccess?: (data: Data) => boolean | undefined
  data?: Data
  isError?: boolean
  isFetching?: boolean
  isLoading?: boolean
  isSuccess?: boolean
  minHeight?: number
  onClose?: () => void
  position?: 'fixed' | 'absolute' | 'relative'
}

export function SuspenseData<Data>({
  data,
  onClose,
  children,
  checkAccess,
  position = 'fixed',
  minHeight,
  isError,
  isFetching,
  isLoading,
  isSuccess
}: IProps<Data>) {
  const prevData = usePrevious(data)
  const [canRender, setCanRender] = useState(false)

  useEffect(() => {
    const lostData = prevData && !data
    const hasAccess = isSuccess && data && (checkAccess ? checkAccess(data) : true)

    if (isError || lostData || (data && !hasAccess)) {
      onClose?.()
    } else if (hasAccess) {
      setCanRender(true)
    }
  }, [onClose, data, checkAccess, prevData, isSuccess, isError])

  if (isLoading || isFetching) {
    return <PageLoading minHeight={minHeight} position={position} />
  }

  if (canRender && data) {
    return children(data)
  }

  if (minHeight) {
    return <PlaceholderCell height={minHeight} />
  }

  return <div />
}
